import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import moment from 'moment'
import type { Moment } from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { useInvalidateDriverRequiredActions } from '@/lib/api/company/drivers/useQueryDriverRequiredActions'
import { BusinessTripModel } from '@/app/models/businessTripModel'
import { GpsDataModel } from '@/app/models/gpsDataModel'
import { TmsDataModel } from '@/app/models/tmsDataModel'
import { GpsFromTachographModel } from '@/app/models/gpsFromTachographModel'
import { TachographLoadUnloadModel } from '@/app/models/tachographLoadUnloadModel'
import { format } from 'date-fns'
import { getGpsDataType } from '@/app/services/gps/gpsDataTypeResolver'
import { getGpsStatus } from '@/app/services/gps/gpsDataStatusResolver'
import { getTmsRouteType } from '@/app/services/tms/tmsRouteTypeResolver'
import { getEventType } from '@/app/services/delegation/tripEventTypeResolver'
import { getVehicleType } from '@/app/services/delegation/vehicleTypeResolver'
import { CountryModel } from '@/app/models/timesheet/countryModel'

import { ExchangeRateModel } from '@/app/models/newDelegation/exchangeRateModel'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { relinkTmsDocuments } from '@/modules/drivers/utils/eventTmsPair'
import { OperationResultModel } from '@/app/models/application/operationResultModel'
import { BusinessTripDetailsModel } from '@/app/models/businessTripDetailsModel'
import { SERVICE_DATE_FORMAT } from '@/app/constants/date'

/** Nazwy jak `TripBulkOperation` na serwerze (= ActionKey menu kontekstowego). */
export type TripBulkOperation =
  | 'changeCountry'
  | 'changeSettlementMonth'
  | 'changeSettlementDate'
  | 'changeExchangeRateDate'
  | 'setSettled'
  | 'setVehicle'
  | 'refreshRates'
  | 'refreshNumber'
  | 'refreshAccommodations'
  | 'copyToTeam'

export interface TripBulkOperationParams {
  countryId?: number
  settlementMonth?: string
  calculationDate?: string
  exchangeRateDate?: string | null
  useDefaultExchangeRateDate?: boolean
  vehicleId?: number
}

export interface TripBulkOperationRequest extends TripBulkOperationParams {
  operation: TripBulkOperation
  trips: Array<{ tripId: number; driverId: number }>
}

export interface GenerateTripsBatchDriverResult {
  driverId: number
  success: boolean
  code?: string
  message?: string
  events: EventModel[]
}

export interface GenerateTripsBatchTaskResult {
  taskId: string
}

export type OverlappingTrip = {
  id: number
  tripNumber: string
  startDate: string
  endDate: string
}

export type CopyBusinessTripsResult = {
  copied: number
  driverFullName: string
  overlapping: OverlappingTrip[]
}

// Kraj pominięty (undefined) = serwer zostawia zapis z importu bez zmian.
export interface TmsSaveItem {
  id: number
  docNumber: string | null
  loadingDate: Moment
  unloadingDate: Moment
  loadingCountry?: string
  unloadingCountry?: string
}

const useBusinessTripService = () => {
  // Zapis i usuniecie delegacji uniewaznia ewidencje miesiaca, wiec znaczniki zadan przy
  // kierowcach musza sie przeladowac od razu - inaczej panel pokazuje stan sprzed zmiany.
  const invalidateDriverRequiredActions = useInvalidateDriverRequiredActions()
  const startLoading = useLoadingStore((s) => s.startLoading)
  const getBusinessTrips = async ({
    companyId,
    drivers,
    startTime,
    endTime,
    filter = 0,
    onlyPolish = false,
  }: {
    companyId: string
    drivers: number[] | string[]
    startTime: string | Date
    endTime: string | Date
    filter?: number
    onlyPolish?: boolean
  }): Promise<BusinessTripModel[]> => {
    const stopLoading = startLoading()
    try {
      // Convert driver IDs to strings as the API expects strings
      const driverIds = drivers.map((id) => String(id))

      // Format dates as plain strings (not encoded) for POST JSON body
      // API expects format: YYYY-MM-DD (e.g., "2025-08-12")
      // Note: date-fns v1 uses uppercase YYYY-MM-DD format
      const startTimeStr =
        startTime instanceof Date
          ? format(startTime, 'YYYY-MM-DD')
          : typeof startTime === 'string'
            ? startTime.split(' ')[0] // Extract just the date part if it contains time
            : String(startTime)
      const endTimeStr =
        endTime instanceof Date
          ? format(endTime, 'YYYY-MM-DD')
          : typeof endTime === 'string'
            ? endTime.split(' ')[0] // Extract just the date part if it contains time
            : String(endTime)

      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/trips`,
        {
          drivers: driverIds,
          startTime: startTimeStr,
          endTime: endTimeStr,
          filter,
          onlyPolish,
        },
      )

      // Map response to BusinessTripModel[]
      // API may return data in response.content.data or response.data
      const data = res.data?.content?.data || res.data?.data || res.data || []

      const toMomentOrNull = (raw: unknown): moment.Moment | null => {
        if (raw == null || raw === '') return null
        let input: unknown = raw
        // .NET: często 7 cyfr po przecinku w sekundach — moment/by przeglądarka bywa kapryśne
        if (typeof input === 'string' && /T\d{2}:\d{2}:\d{2}\.\d+/.test(input)) {
          input = input.replace(/(\.\d{3})\d+/, '$1')
        }
        const m = moment(input as any)
        return m.isValid() ? m : null
      }

      return data.map((trip: any) => ({
        id: trip.id,
        number: trip.number,
        startDate: trip.startDate ? moment(trip.startDate) : null,
        endDate: trip.endDate ? moment(trip.endDate) : null,
        driver: trip.driver,
        registrationNumber: trip.registrationNumber,
        destinationCountry: trip.destinationCountry,
        timeLengthAway: trip.timeLengthAway,
        timeLengthHome: trip.timeLengthHome,
        exchangeRateDate: trip.exchangeRateDate ? moment(trip.exchangeRateDate) : null,
        amount: trip.amount || 0,
        accommodations: trip.accommodations || 0,
        invoices: trip.invoices || 0,
        loans: trip.loans || 0,
        parts: trip.parts || 0,
        isTripSettled: trip.isTripSettled,
        count: trip.count,
        // API: camelCase (Newtonsoft) lub PascalCase (np. proxy / starszy klient)
        creationDate: toMomentOrNull(
          trip.creationDate ?? trip.CreationDate,
        ),
        modificationDate: toMomentOrNull(
          trip.modificationDate ?? trip.ModificationDate,
        ),
      }))
    } catch (err) {
      console.error('Error fetching business trips:', err)
      return []
    } finally {
      stopLoading()
    }
  }

  const deleteBusinessTrips = async ({
    companyId,
    ids,
  }: {
    companyId: string
    ids: number[]
  }): Promise<number[]> => {
    const stopLoading = startLoading()
    try {
      await axiosInstance.post(`api/${companyId}/businesstrip/delete`, { ids })
      invalidateDriverRequiredActions()
      return ids
    } catch (err) {
      console.error('Error deleting business trips:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const copyBusinessTrips = async ({
    companyId,
    ids,
    destinationDriverId,
    ignoreOverlapping = false,
  }: {
    companyId: string
    ids: number[]
    destinationDriverId: number
    ignoreOverlapping?: boolean
  }): Promise<CopyBusinessTripsResult> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/copy`,
        { ids, destinationDriverId, ignoreOverlapping },
      )
      const data = res.data?.content || res.data
      invalidateDriverRequiredActions()
      return {
        copied: data?.copied ?? 0,
        driverFullName: data?.driverFullName ?? '',
        overlapping: data?.overlapping ?? [],
      }
    } catch (err) {
      console.error('Error copying business trips:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const getBusinessTripById = async ({
    companyId,
    tripId,
  }: {
    companyId: string
    tripId: string | number
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/details?id=${tripId}`,
      )
      const data = res.data?.content || res.data || null

      if (!data) {
        return null
      }

      // Map events to ensure tmsRouteType and other fields are properly converted
      const mappedEvents = data.events
        ? relinkTmsDocuments(
            data.events.map(
              (event: any): EventModel => ({
                ...event,
                tripEvent: getEventType(event.tripEvent),
                vehicleType: getVehicleType(event.vehicleType),
                tmsRouteType: getTmsRouteType(event.tmsRouteType),
                date: event.date ? moment(event.date) : undefined,
                secondDate: event.secondDate ? moment(event.secondDate) : null,
              }),
            ),
          )
        : []

      // Normalize country field - API returns 'country' but we use 'destinationCountry' for consistency
      const normalizedData = {
        ...data,
        events: mappedEvents,
        // Map 'country' to 'destinationCountry' if 'destinationCountry' doesn't exist
        destinationCountry: data.destinationCountry || data.country,
      }

      return normalizedData
    } catch (err) {
      console.error('Error fetching business trip details:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const updateBusinessTrip = async ({
    companyId,
    tripId,
    driverId,
    data,
    force = false,
    skipValidation = false,
  }: {
    companyId: string
    tripId: string | number
    driverId?: string | number
    data: any
    /**
     * Save despite the warnings the server flagged as `forceSave`. Mirrors the
     * "Yes" answer to WPF's Yes/No dialogs; the server still refuses when any
     * objection is not forceable.
     */
    force?: boolean
    /**
     * Skip the save validation entirely. Mirrors WPF's trips-list operations,
     * which call TripSaver.SaveTripData directly — ValidateBeforeSave lives only
     * in the trip editor. Not for the editor: there the user gets asked.
     */
    skipValidation?: boolean
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/save`,
        {
          ...data,
          id: tripId,
          force,
          skipValidation,
          ...(driverId !== undefined && { driverId }),
        },
      )
      invalidateDriverRequiredActions()
      return res.data?.content || res.data || null
    } catch (err) {
      console.error('Error updating business trip:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const getCurrencies = async ({
    companyId,
  }: {
    companyId: string
  }): Promise<any[]> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/currencies`,
      )
      return res.data || []
    } catch (err) {
      console.error('Error fetching currencies:', err)
      return []
    } finally {
      stopLoading()
    }
  }

  const getVehicles = async ({
    companyId,
  }: {
    companyId: string
  }): Promise<any[]> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(`api/${companyId}/vehicle/list`)
      return res.data || []
    } catch (err) {
      console.error('Error fetching vehicles:', err)
      return []
    } finally {
      stopLoading()
    }
  }

  const getExchangeRate = async ({
    companyId,
    exchangeRateDate,
    currencyCode,
  }: {
    companyId: string
    exchangeRateDate: string | Date | Moment
    currencyCode: string
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const dateStr =
        exchangeRateDate instanceof Date
          ? format(exchangeRateDate, 'YYYY-MM-DD')
          : moment.isMoment(exchangeRateDate)
            ? exchangeRateDate.format('YYYY-MM-DD')
            : typeof exchangeRateDate === 'string'
              ? exchangeRateDate.split(' ')[0]
              : String(exchangeRateDate)

      const res = await axiosInstance.get(
        `api/${companyId}/businessTrip/exchangeRate?exchangeRateDate=${dateStr}&currencyCode=${currencyCode}`,
      )
      return res.data || null
    } catch (err) {
      console.error('Error fetching exchange rate:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const updateExchangeRates = async ({
    companyId,
    tripId,
    driverId,
    exchangeRateDate,
    accommodations,
    invoices,
    advancePayments,
  }: {
    companyId: string
    tripId?: string | number
    driverId?: string | number
    exchangeRateDate: string | Date | Moment
    accommodations?: any[]
    invoices?: any[]
    advancePayments?: any[]
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const dateStr =
        exchangeRateDate instanceof Date
          ? format(exchangeRateDate, 'YYYY-MM-DD')
          : moment.isMoment(exchangeRateDate)
            ? exchangeRateDate.format('YYYY-MM-DD')
            : typeof exchangeRateDate === 'string'
              ? exchangeRateDate.split(' ')[0]
              : String(exchangeRateDate)

      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/updateExchangeRates`,
        {
          companyId,
          tripId,
          driverId: driverId ? String(driverId) : undefined,
          exchangeRateDate: dateStr,
          accomodations: accommodations || [],
          invoices: invoices || [],
          advancePayments: advancePayments || [],
        },
      )
      return res.data || null
    } catch (err) {
      console.error('Error updating exchange rates:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const getGpsData = async ({
    companyId,
    driverId,
    startTime,
    endTime,
  }: {
    companyId: string
    driverId: string | number
    startTime: string | Date
    endTime: string | Date
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const startTimeStr =
        startTime instanceof Date
          ? format(startTime, 'YYYY-MM-DD')
          : typeof startTime === 'string'
            ? startTime.split(' ')[0]
            : String(startTime)
      let endTimeStr =
        endTime instanceof Date
          ? format(endTime, 'YYYY-MM-DD')
          : typeof endTime === 'string'
            ? endTime.split(' ')[0]
            : String(endTime)

      // Add 1 day to endTime like in legacy implementation
      if (moment.isMoment(endTime)) {
        endTimeStr = moment(endTime).add(1, 'day').format('YYYY-MM-DD')
      } else if (endTime instanceof Date) {
        endTimeStr = format(
          moment(endTime).add(1, 'day').toDate(),
          'YYYY-MM-DD',
        )
      } else if (typeof endTime === 'string') {
        endTimeStr = moment(endTime).add(1, 'day').format('YYYY-MM-DD')
      }

      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/gpsData?&driverId=${driverId}&startTime=${startTimeStr}&endTime=${endTimeStr}`,
      )
      // Handle response structure: response.content.data or response.data
      const data = res.data?.content?.data || res.data?.data || res.data || []
      // Map data fields like in legacy implementation
      return data.map((item: any) => ({
        id: item.id,
        city: item.city,
        destinationCountry: item.destinationCountry,
        foreignCountry: item.foreignCountry,
        transit: item.transit,
        arriveDate: item.arriveDate ? moment(item.arriveDate) : null,
        leaveDate: item.leaveDate ? moment(item.leaveDate) : null,
        type: getGpsDataType(item.type),
        status: getGpsStatus(item.status),
        drivers: item.drivers,
        isBeginOrEnd: item.isBeginOrEnd,
        isMinimalWages: item.isMinimalWages,
        checked: false,
      })) as GpsDataModel[]
    } catch (err) {
      console.error('Error fetching GPS data:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const getVehicleGpsData = async ({
    companyId,
    vehicleId,
    startTime,
    endTime,
  }: {
    companyId: string
    vehicleId: string | number
    startTime: string | Date
    endTime: string | Date
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const startTimeStr =
        startTime instanceof Date
          ? format(startTime, 'YYYY-MM-DD')
          : typeof startTime === 'string'
            ? startTime.split(' ')[0]
            : String(startTime)
      let endTimeStr =
        endTime instanceof Date
          ? format(endTime, 'YYYY-MM-DD')
          : typeof endTime === 'string'
            ? endTime.split(' ')[0]
            : String(endTime)

      // Add 1 day to endTime like in legacy implementation
      if (moment.isMoment(endTime)) {
        endTimeStr = moment(endTime).add(1, 'day').format('YYYY-MM-DD')
      } else if (endTime instanceof Date) {
        endTimeStr = format(
          moment(endTime).add(1, 'day').toDate(),
          'YYYY-MM-DD',
        )
      } else if (typeof endTime === 'string') {
        endTimeStr = moment(endTime).add(1, 'day').format('YYYY-MM-DD')
      }

      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/vehicleGpsData?&vehicleId=${vehicleId}&startTime=${startTimeStr}&endTime=${endTimeStr}`,
      )
      // Handle response structure: response.content.data or response.data
      const data = res.data?.content?.data || res.data?.data || res.data || []
      // Map data fields like in legacy implementation
      return data.map((item: any) => ({
        id: item.id,
        city: item.city,
        destinationCountry: item.destinationCountry,
        foreignCountry: item.foreignCountry,
        transit: item.transit,
        arriveDate: item.arriveDate ? moment(item.arriveDate) : null,
        leaveDate: item.leaveDate ? moment(item.leaveDate) : null,
        type: getGpsDataType(item.type),
        status: getGpsStatus(item.status),
        drivers: item.drivers,
        isBeginOrEnd: item.isBeginOrEnd,
        isMinimalWages: item.isMinimalWages,
        checked: false,
      })) as GpsDataModel[]
    } catch (err) {
      console.error('Error fetching vehicle GPS data:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const getTmsData = async ({
    companyId,
    driverId,
    startTime,
    endTime,
    vehicleId,
  }: {
    companyId: string
    driverId: string | number
    startTime: string | Date
    endTime: string | Date
    vehicleId?: string | number | null
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const startTimeStr =
        startTime instanceof Date
          ? format(startTime, 'YYYY-MM-DD')
          : typeof startTime === 'string'
            ? startTime.split(' ')[0]
            : String(startTime)
      let endTimeStr =
        endTime instanceof Date
          ? format(endTime, 'YYYY-MM-DD')
          : typeof endTime === 'string'
            ? endTime.split(' ')[0]
            : String(endTime)

      // Add 1 day to endTime like in legacy implementation
      if (moment.isMoment(endTime)) {
        endTimeStr = moment(endTime).add(1, 'day').format('YYYY-MM-DD')
      } else if (endTime instanceof Date) {
        endTimeStr = format(
          moment(endTime).add(1, 'day').toDate(),
          'YYYY-MM-DD',
        )
      } else if (typeof endTime === 'string') {
        endTimeStr = moment(endTime).add(1, 'day').format('YYYY-MM-DD')
      }

      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/tmsData?&driverId=${driverId}&startTime=${startTimeStr}&endTime=${endTimeStr}${vehicleId ? `&vehicleId=${vehicleId}` : ''}`,
      )
      console.log('TMS API response:', res.data)

      // Handle response structure: response.content.data or response.data
      let data = res.data?.content?.data || res.data?.data || res.data

      // Ensure data is an array
      if (!data) {
        console.warn('TMS data is null or undefined')
        return []
      }

      if (!Array.isArray(data)) {
        console.warn('TMS data is not an array:', data, 'Type:', typeof data)
        // If it's an object, try to extract an array from it
        if (typeof data === 'object' && data !== null) {
          // Check if there's a nested array property
          const possibleArray = Object.values(data).find((val) =>
            Array.isArray(val),
          )
          if (possibleArray) {
            data = possibleArray
          } else {
            return []
          }
        } else {
          return []
        }
      }

      console.log('TMS data array length:', data.length)

      // Map data fields like in legacy implementation
      return data.map((item: any) => ({
        id: item.id,
        docNumber: item.docNumber,
        loadingDate: moment(item.loadingDate),
        unloadingDate: moment(item.unloadingDate),
        routeType: getTmsRouteType(item.routeType),
        loadingCountry: item.loadingCountry,
        unloadingCountry: item.unloadingCountry,
        transitCountry: item.transitCountry,
        isEmptyLoad: item.isEmptyLoad,
        vehicle: item.vehicle,
        checked: false,
      })) as TmsDataModel[]
    } catch (err) {
      console.error('Error fetching TMS data:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const saveTmsData = async ({
    companyId,
    driverId,
    changed,
    removedIds,
  }: {
    companyId: string
    driverId: string | number
    changed: TmsSaveItem[]
    removedIds: number[]
  }): Promise<void> => {
    const stopLoading = startLoading()
    try {
      // Daty TMS są w czasie lokalnym, bez strefy - jak przy odczycie.
      const toLocal = (date: Moment) => date.format('YYYY-MM-DDTHH:mm:ss')
      await axiosInstance.post(`api/${companyId}/businesstrip/tmsData`, {
        driverId: Number(driverId),
        changed: changed.map((item) => ({
          ...item,
          loadingDate: toLocal(item.loadingDate),
          unloadingDate: toLocal(item.unloadingDate),
        })),
        removedIds,
      })
    } finally {
      stopLoading()
    }
  }

  const getGpsFromTachograph = async ({
    companyId,
    driverId,
    startTime,
    endTime,
  }: {
    companyId: string
    driverId: string | number
    startTime: string | Date
    endTime: string | Date
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const startTimeStr =
        startTime instanceof Date
          ? format(startTime, 'YYYY-MM-DD')
          : typeof startTime === 'string'
            ? startTime.split(' ')[0]
            : String(startTime)
      let endTimeStr =
        endTime instanceof Date
          ? format(endTime, 'YYYY-MM-DD')
          : typeof endTime === 'string'
            ? endTime.split(' ')[0]
            : String(endTime)

      // Add 1 day to endTime like in legacy implementation
      if (moment.isMoment(endTime)) {
        endTimeStr = moment(endTime).add(1, 'day').format('YYYY-MM-DD')
      } else if (endTime instanceof Date) {
        endTimeStr = format(
          moment(endTime).add(1, 'day').toDate(),
          'YYYY-MM-DD',
        )
      } else if (typeof endTime === 'string') {
        endTimeStr = moment(endTime).add(1, 'day').format('YYYY-MM-DD')
      }

      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/gpsFromTachograph?&driverId=${driverId}&startTime=${startTimeStr}&endTime=${endTimeStr}`,
      )
      // Handle response structure: response.content.data or response.data
      const data = res.data?.content?.data || res.data?.data || res.data || []
      // Map data fields like in legacy implementation
      return data.map((item: any) => ({
        id: item.id,
        country: item.country,
        date: moment(item.date),
        isArrival: item.isArrival,
        isTransit: item.isTransit,
        checked: false,
      })) as GpsFromTachographModel[]
    } catch (err) {
      console.error('Error fetching GPS from tachograph:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  // dateTo włącznie - serwer dostaje koniec okresu jako następny dzień.
  const getTachographLoadUnload = async ({
    companyId,
    driverId,
    dateFrom,
    dateTo,
    vehicleId,
  }: {
    companyId: string
    driverId: string | number
    dateFrom: string
    dateTo: string
    vehicleId?: string | number | null
  }): Promise<TachographLoadUnloadModel[]> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/tachographLoadUnload`,
        {
          params: {
            driverId,
            startTime: moment(dateFrom).format('YYYY-MM-DD'),
            endTime: moment(dateTo).add(1, 'day').format('YYYY-MM-DD'),
            vehicleId: vehicleId || undefined,
          },
        },
      )
      const data = res.data?.content?.data || res.data?.data || res.data || []
      return data.map((item: any) => ({
        ...item,
        date: moment(item.date),
      }))
    } finally {
      stopLoading()
    }
  }

  const getCountriesWithMinimalPayment = async ({
    companyId,
  }: {
    companyId: string
  }): Promise<string[]> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/minimalPaymentCountries`,
      )
      // API may return data in response.content.data or response.data
      const data = res.data?.content?.data || res.data?.data || res.data || []
      return data
    } catch (err) {
      console.error('Error fetching countries with minimal payment:', err)
      return []
    } finally {
      stopLoading()
    }
  }

  const getCountries = async ({
    companyId,
  }: {
    companyId: string
  }): Promise<CountryModel[]> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(`api/${companyId}/country/countries`)
      // API may return data in response.content.data or response.data
      const data = res.data?.content?.data || res.data?.data || res.data || []
      return data as CountryModel[]
    } catch (err) {
      console.error('Error fetching countries:', err)
      return []
    } finally {
      stopLoading()
    }
  }

  const getMultipleDestinationCountries = async ({
    companyId,
  }: {
    companyId: string
  }): Promise<boolean> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/setMultipleDestinationCountries`,
      )
      // API may return data in response.content.data or response.data
      const data =
        res.data?.content?.data ?? res.data?.data ?? res.data ?? false
      return Boolean(data)
    } catch (err) {
      console.error(
        'Error fetching multiple destination countries config:',
        err,
      )
      return false
    } finally {
      stopLoading()
    }
  }

  const changeExchangeRates = async ({
    companyId,
    rates,
  }: {
    companyId: string
    rates: ExchangeRateModel[]
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/changeExchangeRate`,
        {
          companyId,
          rates: rates.map((r) => ({
           exchangeRateDate:
              moment.isMoment(r.exchangeRateDate) ||
              r.exchangeRateDate instanceof Date
                ? moment(r.exchangeRateDate).format(SERVICE_DATE_FORMAT)
                : typeof r.exchangeRateDate === 'string'
                  ? r.exchangeRateDate.split(' ')[0]
                  : String(r.exchangeRateDate),
            currencyCode: r.currencyCode,
            rate: r.rate,
          })),
        },
      )
      return res.data?.content?.data || res.data?.data || res.data || null
    } catch (err) {
      console.error('Error changing exchange rates:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const saveGrouped = async ({
    companyId,
    id,
    driverId,
    defaultStartDate,
    calculationDate,
    country,
    events,
    force,
    forceSave = false,
    withPreviousExchangeRateDate,
    forceCopyToCoDriver,
    copyToCoDriver,
    checkedTripCoDrivers,
  }: {
    companyId: string
    id: string
    driverId: string | number
    defaultStartDate: string | Date | Moment
    calculationDate?: string | Date | Moment | null
    country: CountryModel
    events: EventModel[]
    /** Keeps the supplied exchange rate date — NOT "save despite warnings". */
    force: boolean
    /**
     * Save despite non-settled trips overlapping this period. Mirrors "Yes" in
     * WPF's TripGroupViewModel.OnSave() dialog; settled trips still block.
     */
    forceSave?: boolean
    withPreviousExchangeRateDate: boolean
    forceCopyToCoDriver: boolean
    copyToCoDriver: boolean
    checkedTripCoDrivers: boolean[]
  }): Promise<OperationResultModel[]> => {
    const stopLoading = startLoading()
    try {
      const toDateStr = (value: string | Date | Moment) =>
        value instanceof Date
          ? format(value, 'YYYY-MM-DD')
          : moment.isMoment(value)
            ? value.format(SERVICE_DATE_FORMAT)
            : typeof value === 'string'
              ? value.split(' ')[0]
              : String(value)

      const dateStr = toDateStr(defaultStartDate)

      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/saveGrouped`,
        {
          companyId,
          id,
          driverId: String(driverId),
          defaultStartDate: dateStr,
          calculationDate: calculationDate ? toDateStr(calculationDate) : null,
          country,
          events,
          force,
          forceSave,
          withPreviousExchangeRateDate,
          forceCopyToCoDriver,
          copyToCoDriver,
          checkedTripCoDrivers,
        },
      )
      const data = res.data?.content?.data || res.data?.data || res.data || []
      // Tedy idzie tworzenie i zapis delegacji z ekranu grupowego oraz "Dodaj delegacje" -
      // czyli sciezka, ktora tworzy delegacje od zera, nie tylko edytuje istniejaca.
      invalidateDriverRequiredActions()
      return data as OperationResultModel[]
    } catch (err: any) {
      console.error('Error saving grouped business trip:', err)
      // A validation failure comes back as HTTP 400 with the OperationResult
      // list as the body - that is a meaningful result the caller inspects.
      const payload = err?.response?.data
      const results =
        payload?.content?.data ?? payload?.data ?? payload ?? null
      if (Array.isArray(results)) {
        return results as OperationResultModel[]
      }
      // Anything else (network failure, 500, HTML error page) is a real
      // failure - rethrow so callers do not treat it as a successful save.
      throw err
    } finally {
      stopLoading()
    }
  }

  const generateTrips = async ({
    companyId,
    driverId,
    defaultStartDate,
    defaultEndDate,
    events,
    generationMode,
  }: {
    companyId: string
    driverId: string | number
    defaultStartDate: string | Date | Moment
    defaultEndDate: string | Date | Moment
    events: EventModel[]
    generationMode: number
  }): Promise<EventModel[]> => {
    const stopLoading = startLoading()
    try {
      const startDateStr =
        defaultStartDate instanceof Date
          ? format(defaultStartDate, 'YYYY-MM-DD')
          : moment.isMoment(defaultStartDate)
            ? defaultStartDate.format(SERVICE_DATE_FORMAT)
            : typeof defaultStartDate === 'string'
              ? defaultStartDate.split(' ')[0]
              : String(defaultStartDate)

      const endDateStr =
        defaultEndDate instanceof Date
          ? format(defaultEndDate, 'YYYY-MM-DD')
          : moment.isMoment(defaultEndDate)
            ? defaultEndDate.format(SERVICE_DATE_FORMAT)
            : typeof defaultEndDate === 'string'
              ? defaultEndDate.split(' ')[0]
              : String(defaultEndDate)

      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/generateTrips`,
        {
          companyId,
          driverId: String(driverId),
          defaultStartDate: startDateStr,
          defaultEndDate: endDateStr,
          events,
          generationMode,
        },
      )
      const data = res.data?.content?.data || res.data?.data || res.data || []
      return data as EventModel[]
    } catch (err) {
      console.error('Error generating trips:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const generateTripsBatch = async ({
    companyId,
    driverIds,
    defaultStartDate,
    defaultEndDate,
    events,
    generationMode,
  }: {
    companyId: string
    driverIds: Array<string | number>
    defaultStartDate: string | Date | Moment
    defaultEndDate: string | Date | Moment
    events: EventModel[]
    generationMode: number
  }): Promise<GenerateTripsBatchDriverResult[]> => {
    const stopLoading = startLoading()
    try {
      const startDateStr =
        defaultStartDate instanceof Date
          ? format(defaultStartDate, 'YYYY-MM-DD')
          : moment.isMoment(defaultStartDate)
            ? defaultStartDate.format(SERVICE_DATE_FORMAT)
            : typeof defaultStartDate === 'string'
              ? defaultStartDate.split(' ')[0]
              : String(defaultStartDate)

      const endDateStr =
        defaultEndDate instanceof Date
          ? format(defaultEndDate, 'YYYY-MM-DD')
          : moment.isMoment(defaultEndDate)
            ? defaultEndDate.format(SERVICE_DATE_FORMAT)
            : typeof defaultEndDate === 'string'
              ? defaultEndDate.split(' ')[0]
              : String(defaultEndDate)

      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/generateTripsBatch`,
        {
          companyId,
          driverIds: driverIds.map((driverId) => Number(driverId)),
          defaultStartDate: startDateStr,
          defaultEndDate: endDateStr,
          events,
          generationMode,
        },
      )
      const data = res.data?.content?.data || res.data?.data || res.data || []
      return data as GenerateTripsBatchDriverResult[]
    } catch (err) {
      console.error('Error generating trips batch:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  // Pre-flight for the generate flow: drivers with no active contract in the
  // period (the ones the batch would only report as NO_ACTIVE_CONTRACT after
  // minutes of generation). Mirrors the timesheet screen's pre-calculation
  // missing-contracts dialog.
  const checkTripActiveContracts = async ({
    companyId,
    driverIds,
    defaultStartDate,
    defaultEndDate,
  }: {
    companyId: string
    driverIds: Array<string | number>
    defaultStartDate: string | Date | Moment
    defaultEndDate: string | Date | Moment
  }): Promise<{ driverId: number; driverName: string }[]> => {
    const toDateStr = (value: string | Date | Moment) =>
      value instanceof Date
        ? format(value, 'YYYY-MM-DD')
        : moment.isMoment(value)
          ? value.format(SERVICE_DATE_FORMAT)
          : typeof value === 'string'
            ? value.split(' ')[0]
            : String(value)

    const res = await axiosInstance.post(
      `api/${companyId}/businesstrip/checkActiveContracts`,
      {
        companyId,
        driverIds: driverIds.map((driverId) => Number(driverId)),
        defaultStartDate: toDateStr(defaultStartDate),
        defaultEndDate: toDateStr(defaultEndDate),
        events: [],
        generationMode: 0,
      },
    )
    const data = res.data?.content?.data || res.data?.data || res.data
    return (data?.drivers ?? []) as { driverId: number; driverName: string }[]
  }

  // Which of the selected drivers drove in a crew, so the generate dialog can ask the
  // co-driver question once up front instead of the batch handing every crew driver
  // back unsaved.
  const checkTripCoDrivers = async ({
    companyId,
    driverIds,
    defaultStartDate,
    defaultEndDate,
  }: {
    companyId: string
    driverIds: Array<string | number>
    defaultStartDate: string | Date | Moment
    defaultEndDate: string | Date | Moment
  }): Promise<{ driverId: number; driverName: string }[]> => {
    const toDateStr = (value: string | Date | Moment) =>
      value instanceof Date
        ? format(value, 'YYYY-MM-DD')
        : moment.isMoment(value)
          ? value.format(SERVICE_DATE_FORMAT)
          : typeof value === 'string'
            ? value.split(' ')[0]
            : String(value)

    const res = await axiosInstance.post(
      `api/${companyId}/businesstrip/checkCoDrivers`,
      {
        companyId,
        driverIds: driverIds.map((driverId) => Number(driverId)),
        defaultStartDate: toDateStr(defaultStartDate),
        defaultEndDate: toDateStr(defaultEndDate),
        events: [],
        generationMode: 0,
      },
    )
    const data = res.data?.content?.data || res.data?.data || res.data
    return (data?.drivers ?? []) as { driverId: number; driverName: string }[]
  }

  const generateTripsBatchTask = async ({
    companyId,
    driverIds,
    defaultStartDate,
    defaultEndDate,
    events,
    generationMode,
    copyToCoDriver = false,
    withPreviousExchangeRateDate = false,
  }: {
    companyId: string
    driverIds: Array<string | number>
    defaultStartDate: string | Date | Moment
    defaultEndDate: string | Date | Moment
    events: EventModel[]
    generationMode: number
    /** Answered before the batch starts; crew drivers are then saved inside it. */
    copyToCoDriver?: boolean
    /** Powtórka paczki, która padła na braku kursu NBP — z kursem z ostatniego dnia roboczego. */
    withPreviousExchangeRateDate?: boolean
  }): Promise<GenerateTripsBatchTaskResult> => {
    const stopLoading = startLoading()
    try {
      const startDateStr =
        defaultStartDate instanceof Date
          ? format(defaultStartDate, 'YYYY-MM-DD')
          : moment.isMoment(defaultStartDate)
            ? defaultStartDate.format(SERVICE_DATE_FORMAT)
            : typeof defaultStartDate === 'string'
              ? defaultStartDate.split(' ')[0]
              : String(defaultStartDate)

      const endDateStr =
        defaultEndDate instanceof Date
          ? format(defaultEndDate, 'YYYY-MM-DD')
          : moment.isMoment(defaultEndDate)
            ? defaultEndDate.format(SERVICE_DATE_FORMAT)
            : typeof defaultEndDate === 'string'
              ? defaultEndDate.split(' ')[0]
              : String(defaultEndDate)

      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/generateTripsBatchTask`,
        {
          companyId,
          driverIds: driverIds.map((driverId) => Number(driverId)),
          defaultStartDate: startDateStr,
          defaultEndDate: endDateStr,
          events,
          generationMode,
          copyToCoDriver,
          withPreviousExchangeRateDate,
        },
      )
      const data = res.data?.content?.data || res.data?.data || res.data
      return data as GenerateTripsBatchTaskResult
    } catch (err) {
      console.error('Error queuing trips batch:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  // Finishing a group generation once the user answered the co-driver dialog.
  // One request for the whole selection: the server loops the drivers and reports
  // through the same task the generation used, instead of the browser firing a
  // saveGrouped POST per driver.
  const saveGroupedBatchTask = async ({
    companyId,
    drivers,
    defaultStartDate,
    force = false,
    forceSave = false,
    withPreviousExchangeRateDate = false,
    copyToCoDriver,
  }: {
    companyId: string
    drivers: Array<{
      driverId: string | number
      events: EventModel[]
      checkedTripCoDrivers: boolean[]
      defaultStartDate?: string | Date | Moment
    }>
    defaultStartDate: string | Date | Moment
    force?: boolean
    forceSave?: boolean
    withPreviousExchangeRateDate?: boolean
    copyToCoDriver: boolean
  }): Promise<GenerateTripsBatchTaskResult> => {
    const stopLoading = startLoading()
    try {
      const toDateStr = (value: string | Date | Moment | undefined) =>
        value === undefined || value === null
          ? undefined
          : value instanceof Date
            ? format(value, 'YYYY-MM-DD')
            : moment.isMoment(value)
              ? value.format(SERVICE_DATE_FORMAT)
              : typeof value === 'string'
                ? value.split(' ')[0]
                : String(value)

      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/saveGroupedBatchTask`,
        {
          companyId,
          defaultStartDate: toDateStr(defaultStartDate),
          force,
          forceSave,
          withPreviousExchangeRateDate,
          copyToCoDriver,
          drivers: drivers.map((driver) => ({
            driverId: Number(driver.driverId),
            defaultStartDate: toDateStr(driver.defaultStartDate),
            events: driver.events,
            checkedTripCoDrivers: driver.checkedTripCoDrivers,
          })),
        },
      )
      const data = res.data?.content?.data || res.data?.data || res.data
      return data as GenerateTripsBatchTaskResult
    } catch (err) {
      console.error('Error queuing grouped trips save batch:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const generateAccommodations = async ({
    companyId,
    driverId,
    exchangeRateDate,
    country,
    events,
    invoices,
    stopDownloadingRates = false,
  }: {
    companyId: string
    driverId: string | number
    exchangeRateDate: string
    country: CountryModel | null
    events: any[]
    invoices: any[]
    stopDownloadingRates?: boolean
  }): Promise<any[]> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/generateAccomodations`,
        {
          companyId,
          driverId,
          exchangeRateDate,
          country,
          events: events.map((e: any, i: number) => ({ ...e, lp: e.lp ?? i })),
          invoices,
          stopDownloadingRates,
        },
      )
      const data = res.data?.content?.data || res.data?.data || res.data || []
      return data
    } catch (err) {
      console.error('Error generating accommodations:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const generateAllowances = async ({
    companyId,
    driverId,
    exchangeRateDate,
    country,
    events,
    alimentations,
    invoices,
    accomodations = [],
    stopDownloadingRates = false,
    preserveCostInvoices = false,
    preserveAccommodationInvoices = false,
  }: {
    companyId: string
    driverId: string | number
    exchangeRateDate: string
    country: CountryModel | null
    events: any[]
    alimentations: any[]
    invoices: any[]
    accomodations?: any[]
    stopDownloadingRates?: boolean
    preserveCostInvoices?: boolean
    preserveAccommodationInvoices?: boolean
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/generateAlimentations`,
        {
          companyId,
          driverId: String(driverId),
          exchangeRateDate,
          country,
          events,
          alimentations,
          invoices,
          accomodations,
          stopDownloadingRates,
          preserveCostInvoices,
          preserveAccommodationInvoices,
        },
      )
      const data = res.data?.content?.data || res.data?.data || res.data || {}
      return data
    } catch (err) {
      console.error('Error generating allowances:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  /**
   * Odświeżenie kosztów delegacji po stronie serwera — delegacja jest wczytywana
   * z bazy, więc nie ma rundy przez przeglądarkę (żadnej konwersji stref na
   * zdarzeniach) i nie są przeliczane diety. Parytet z WPF
   * `TripsListViewModel.RefreshSelectedTripsCostsExecute`.
   */
  const refreshTripCosts = async ({
    companyId,
    tripId,
    driverId,
  }: {
    companyId: string
    tripId: string | number
    driverId: string | number
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/refreshCosts`,
        { tripId: Number(tripId), driverId: Number(driverId) },
      )
      // Odswiezenie kosztow zmienia delegacje i unieważnia ewidencje (TripSaver oznacza
      // miesiace jako nieaktualne), wiec wymagane akcje kierowcy trzeba przeladowac -
      // tak samo jak po zapisie i usunieciu delegacji.
      invalidateDriverRequiredActions()
      return res.data?.content ?? res.data ?? null
    } catch (err) {
      console.error('Error refreshing trip costs:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  const generateTripNumber = async ({
    companyId,
    id,
    driverId,
    country,
    events,
  }: {
    companyId: string
    id: string | number
    driverId: string | number
    country: any
    events: any[]
  }): Promise<any> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/generateNumber`,
        {
          id: String(id),
          driverId: String(driverId),
          country,
          events,
        },
      )
      return res.data?.content || res.data || null
    } catch (err) {
      console.error('Error generating trip number:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  /**
   * Masowe odświeżenie kosztów: jedno żądanie na całe zaznaczenie zamiast jednego
   * na delegację. Pracę wykonuje fileprocessor, więc przeżywa zamknięcie karty;
   * zwracany `taskId` śledzimy przez TaskContext, tak jak generowanie delegacji.
   */
  const refreshTripCostsBatchTask = async ({
    companyId,
    trips,
  }: {
    companyId: string
    /** Kierowca jest potrzebny serwerowi: partycje batcha idą po kierowcach. */
    trips: Array<{ tripId: string | number; driverId: string | number }>
  }): Promise<{ taskId: string } | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/refreshCostsBatchTask`,
        {
          trips: trips.map((trip) => ({
            tripId: Number(trip.tripId),
            driverId: Number(trip.driverId),
          })),
        },
      )
      const data = res.data?.content?.data || res.data?.data || res.data
      return data as { taskId: string }
    } catch (err) {
      console.error('Error queuing trip costs refresh:', err)
      throw err
    } finally {
      stopLoading()
    }
  }

  /**
   * Operacja z menu kontekstowego listy delegacji wykonana na serwerze (parytet z WPF
   * TripsListViewModel). Jedna delegacja - od razu; wiele - jako zadanie w fileprocessorze.
   */
  const runTripBulkOperation = async (
    companyId: string,
    request: TripBulkOperationRequest,
  ): Promise<Array<{ tripId: number; createdTrips?: number }>> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/bulkOperation`,
        request,
      )
      invalidateDriverRequiredActions()
      const data = res.data?.content ?? res.data
      return Array.isArray(data) ? data : []
    } finally {
      stopLoading()
    }
  }

  const startTripBulkOperationTask = async (
    companyId: string,
    request: TripBulkOperationRequest,
  ): Promise<{ taskId: string } | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/businesstrip/bulkOperationBatchTask`,
        request,
      )
      const data = res.data?.content?.data || res.data?.data || res.data
      return data as { taskId: string }
    } finally {
      stopLoading()
    }
  }

  const getBusinessTripDetails = async ({
    companyId,
    id,
  }: {
    companyId: string
    id: string | number
  }): Promise<BusinessTripDetailsModel | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `api/${companyId}/businesstrip/details?id=${id}`,
      )
      const data = res.data?.content || res.data || null
      return data as BusinessTripDetailsModel | null
    } catch (err) {
      console.error('Error fetching business trip details:', err)
      return null
    } finally {
      stopLoading()
    }
  }

  return {
    copyBusinessTrips,
    getBusinessTrips,
    deleteBusinessTrips,
    getBusinessTripById,
    getBusinessTripDetails,
    generateTripNumber,
    updateBusinessTrip,
    getCurrencies,
    getVehicles,
    getExchangeRate,
    updateExchangeRates,
    getGpsData,
    getVehicleGpsData,
    getTmsData,
    saveTmsData,
    getGpsFromTachograph,
    getTachographLoadUnload,
    getCountriesWithMinimalPayment,
    getCountries,
    getMultipleDestinationCountries,
    changeExchangeRates,
    saveGrouped,
    generateTrips,
    generateTripsBatch,
    generateTripsBatchTask,
    saveGroupedBatchTask,
    checkTripActiveContracts,
    checkTripCoDrivers,
    generateAccommodations,
    generateAllowances,
    refreshTripCosts,
    refreshTripCostsBatchTask,
    runTripBulkOperation,
    startTripBulkOperationTask,
  }
}

export default useBusinessTripService
